import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  effect,
  computed,
} from "@angular/core";
import {
  ActivatedRoute,
  Router,
  RouterOutlet,
  NavigationEnd,
} from "@angular/router";
import { ToastModule } from "primeng/toast";
import { ThemeService } from "../../../core/services/theme.service";
import { ScanditScannerService } from "../../services/scandit-scanner.service";
import { ScanIframeService } from "../../services/scan-iframe.service";
import { ScannedProductsService } from "../../services/scanned-products.service";
import { ScanSessionTimeoutService } from "../../services/scan-session-timeout.service";
import { SessionTimeoutDialogComponent } from "../session-timeout-dialog/session-timeout-dialog.component";
import { ScanIncomingMessageType } from "../../models/scan-iframe.contract";
import { filter, takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { routes } from "../../../../environments/environment.routes";
import { LOCAL_STORAGE_SCAN_MODE_KEY } from "../../../core/utils/app.constants.utils";

@Component({
  selector: "twx-scan-layout",
  imports: [RouterOutlet, ToastModule, SessionTimeoutDialogComponent],
  templateUrl: "./scan-layout.component.html",
  styleUrl: "./scan-layout.component.scss",
})
export class ScanLayoutComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private themeService = inject(ThemeService);
  private scanner = inject(ScanditScannerService);
  private scanIframeService = inject(ScanIframeService);
  private scannedProductsService = inject(ScannedProductsService);
  private sessionTimeoutService = inject(ScanSessionTimeoutService);

  private sessionId: string | null = null;
  private sessionStartTime: Date | null = null;
  private currentScanMode: "native" | "camera" = "native";
  private destroy$ = new Subject<void>();

  // Expose timeout dialog visibility as a signal for the template
  // Don't show the dialog on device-not-supported page (user can't scan anyway)
  readonly showTimeoutDialog = computed(() => {
    if (!this.sessionTimeoutService.showWarningDialog()) {
      return false;
    }
    return !this.router.url.includes(routes.frontend.deviceNotSupported);
  });

  constructor() {
    // Watch for session timeout and auto-end the session
    effect(() => {
      if (this.sessionTimeoutService.sessionTimedOut()) {
        this.handleSessionTimeout();
      }
    });
  }

  ngOnInit(): void {
    this.applyScanPageTheme();
    this.startScanSession();
    this.setupRouterSubscription();
    this.scanIframeService.startListening();
    this.setupParentMessageListeners();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.scanIframeService.stopListening();
    this.sessionTimeoutService.stopTracking();
    this.endScanSession();
    this.removeScanPageTheme();
    this.scanner.cleanup();
  }

  private applyScanPageTheme(): void {
    const body = document.body;
    const theme = this.themeService.configValue;

    const heightParam = this.route.snapshot.queryParams["height"];
    console.log("Height parameter found:", heightParam);
    const parsedHeightParam = heightParam ?? "400px";

    body.style.setProperty(
      "--tactiq-scan-container-max-height",
      parsedHeightParam,
    );
    body.style.setProperty("height", parsedHeightParam);
    body.style.setProperty("maxHeight", parsedHeightParam);
    body.style.setProperty("overflow", "hidden");

    body.classList.add("no-scroll");

    body.style.setProperty(
      "--tactiq-scan-page-bg-color",
      theme.scanPageBgColor,
    );
    body.style.setProperty(
      "--tactiq-scan-page-text-color",
      theme.scanPageTextColor,
    );
    body.style.setProperty(
      "--tactiq-scan-page-primary-color",
      theme.scanPagePrimaryColor,
    );
    body.style.setProperty(
      "--tactiq-scan-page-accent-color",
      theme.scanPageAccentColor,
    );
    body.style.setProperty(
      "--tactiq-scan-page-scan-line-color",
      theme.scanPageScanLineColor,
    );

    body.style.setProperty(
      "--tactiq-scan-page-primary-button-border-color",
      theme.scanPagePrimaryButtonBorderColor,
    );

    body.style.setProperty(
      "--tactiq-scan-page-secondary-button-border-color",
      theme.scanPageSecondaryButtonBorderColor,
    );

    body.style.backgroundColor = theme.scanPageBgColor;
    body.style.color = theme.scanPageTextColor;
  }

  private removeScanPageTheme(): void {
    const body = document.body;

    body.classList.remove("no-scroll");

    body.style.removeProperty("--tactiq-scan-page-bg-color");
    body.style.removeProperty("--tactiq-scan-page-text-color");
    body.style.removeProperty("--tactiq-scan-page-primary-color");
    body.style.removeProperty("--tactiq-scan-page-accent-color");
    body.style.removeProperty("--tactiq-scan-page-scan-line-color");
    body.style.removeProperty("--tactiq-scan-page-primary-button-border-color");
    body.style.removeProperty(
      "--tactiq-scan-page-secondary-button-border-color",
    );

    body.style.removeProperty("background-color");
    body.style.removeProperty("color");
  }

  /**
   * Setup router subscription to handle mode changes reactively
   */
  private setupRouterSubscription(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.handleRouteChange();
      });
  }

  /**
   * Set up listeners for specific messages from parent iframe
   */
  private setupParentMessageListeners(): void {
    // Listen for SCAN_RESUMED message from parent
    // When received, navigate user to the first step of scanning using their preferred mode
    this.scanIframeService
      .onMessage(ScanIncomingMessageType.SCAN_RESUMED)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const storedMode = localStorage.getItem(LOCAL_STORAGE_SCAN_MODE_KEY);
        const scanRoute =
          storedMode === "camera"
            ? routes.frontend.scanCamera
            : routes.frontend.scanNative;

        this.router.navigate([routes.frontend.scan, scanRoute], {
          queryParamsHandling: "preserve",
        });
      });
  }

  /**
   * Start a new scan session
   */
  private startScanSession(): void {
    this.sessionId = this.generateSessionId();
    this.sessionStartTime = new Date();

    // Determine scan mode based on current URL route
    this.currentScanMode = this.getScanModeFromRoute();

    // Start session timeout tracking
    this.sessionTimeoutService.startTracking();

    // Send session start event
    this.scanIframeService.sendScanSessionStart({
      sessionId: this.sessionId,
      tenantId: this.getTenantId(),
      startTime: this.sessionStartTime.toISOString(),
      mode: this.currentScanMode,
    });
  }

  /**
   * End the current scan session
   */
  private endScanSession(): void {
    if (this.sessionId && this.sessionStartTime) {
      const endTime = new Date();
      const duration = endTime.getTime() - this.sessionStartTime.getTime();

      // Send session end event
      this.scanIframeService.sendScanSessionEnd({
        sessionId: this.sessionId,
        tenantId: this.getTenantId(),
        endTime: endTime.toISOString(),
        totalScans: this.getTotalScans(), // This would need to be tracked
        duration: duration,
      });
    }
  }

  /**
   * Handle scan mode switching
   */
  public switchScanMode(newMode: "native" | "camera"): void {
    if (this.currentScanMode !== newMode) {
      const previousMode = this.currentScanMode;
      this.currentScanMode = newMode;

      // Send mode switch event
      this.scanIframeService.sendScanModeSwitched({
        previousMode: previousMode,
        newMode: newMode,
        timestamp: new Date().toISOString(),
        reason: "user_selection",
      });
    }
  }

  /**
   * Handle mode switch due to route changes
   */
  private handleRouteChange(): void {
    const newMode = this.getScanModeFromRoute();

    // Persist the user's scan mode preference for next session
    this.persistScanModePreference();

    if (this.currentScanMode !== newMode) {
      const previousMode = this.currentScanMode;
      this.currentScanMode = newMode;

      // Send mode switch event
      this.scanIframeService.sendScanModeSwitched({
        previousMode: previousMode,
        newMode: newMode,
        timestamp: new Date().toISOString(),
        reason: "user_selection", // Route change indicates user navigation
      });
    }
  }

  /**
   * Handle mode switch due to error fallback
   */
  public handleErrorFallback(): void {
    if (this.currentScanMode === "native") {
      const previousMode = this.currentScanMode;
      this.currentScanMode = "camera";

      // Send mode switch event
      this.scanIframeService.sendScanModeSwitched({
        previousMode: previousMode,
        newMode: "camera",
        timestamp: new Date().toISOString(),
        reason: "error_fallback",
      });
    }
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getTenantId(): string {
    const config = this.scanIframeService.getCurrentConfig();
    return config?.tenantId || "unknown";
  }

  private getScanModeFromRoute(): "native" | "camera" {
    const url = this.route.snapshot.url.join("/");

    if (url.includes("/nat")) {
      return "native";
    } else if (url.includes("/cam")) {
      return "camera";
    }

    return "native";
  }

  private getTotalScans(): number {
    return this.scannedProductsService.productCount();
  }

  /**
   * Persist the current scan mode to localStorage so the user is
   * automatically routed to their last-used mode on the next visit.
   * Only saves for actual scanning routes (native/camera), not results or error pages.
   */
  private persistScanModePreference(): void {
    const url = this.router.url;

    if (url.includes(`/${routes.frontend.scanNative}`)) {
      localStorage.setItem(LOCAL_STORAGE_SCAN_MODE_KEY, "native");
    } else if (url.includes(`/${routes.frontend.scanCamera}`)) {
      localStorage.setItem(LOCAL_STORAGE_SCAN_MODE_KEY, "camera");
    }
  }

  /**
   * Handle user clicking "Yes" on the timeout dialog
   * User wants to continue scanning
   */
  onContinueScanning(): void {
    this.sessionTimeoutService.userConfirmedStillScanning();
  }

  /**
   * Handle user clicking "No" on the timeout dialog
   * User wants to end the session
   */
  onEndSessionRequested(): void {
    this.sessionTimeoutService.userRequestedEndSession();
  }

  /**
   * Handle session timeout (either user clicked No or 30min timeout reached)
   */
  private handleSessionTimeout(): void {
    const hasProducts = this.scannedProductsService.productCount() > 0;
    const productCount = this.scannedProductsService.productCount();
    const config = this.scanIframeService.getCurrentConfig();
    const tenantId = config?.tenantId || "unknown";

    // Notify the timeout service that session ended (resets for next session)
    this.sessionTimeoutService.onSessionEnded();

    if (hasProducts) {
      // User has scanned products - navigate to results first, then send SCAN_COMPLETE event
      this.router
        .navigate([routes.frontend.scan, routes.frontend.scanResults], {
          queryParamsHandling: "preserve",
        })
        .then(() => {
          this.scanIframeService.sendScanComplete({
            tenantId,
            timestamp: new Date().toISOString(),
            hasProducts: true,
            productCount,
            action: "show_results",
          });
        });
    } else {
      // User didn't scan anything - send SCAN_SESSION_END event (no navigation)
      this.endScanSession();
    }

    // Reset session tracking for potential new session
    this.sessionId = null;
    this.sessionStartTime = null;
  }
}
